import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isSmallScreen = window.innerWidth <= 768;
      return isMobileDevice || isSmallScreen;
    };

    const mobileCheck = checkMobile();
    setIsMobile(mobileCheck);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      return; // Already installed, don't show
    }

    // Only proceed if mobile
    if (!mobileCheck) {
      return;
    }

    // Track first visit date
    const firstVisitDate = localStorage.getItem('pwa-first-visit-date');
    const lastShownDate = localStorage.getItem('pwa-last-shown-date');
    const now = Date.now();
    
    if (!firstVisitDate) {
      // First time visitor - store the date
      localStorage.setItem('pwa-first-visit-date', now.toString());
    }

    // Calculate days since first visit
    const daysSinceFirstVisit = firstVisitDate 
      ? (now - parseInt(firstVisitDate)) / (1000 * 60 * 60 * 24)
      : 0;

    // Check if user has skipped the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedDate = localStorage.getItem('pwa-install-dismissed-date');

    // Helper function to check if two timestamps are on the same day
    const isSameDay = (timestamp1, timestamp2) => {
      const date1 = new Date(timestamp1);
      const date2 = new Date(timestamp2);
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate();
    };

    // Logic for showing prompt:
    // 1. First 3 days: Show once per day (not multiple times per day)
    // 2. After 3 days: Only show if user skipped AND 7 days have passed since skip
    let shouldShow = false;

    if (daysSinceFirstVisit < 3) {
      // First 3 days: Show once per day
      // Check if we've already shown it today
      if (!lastShownDate || !isSameDay(now, parseInt(lastShownDate))) {
        // Haven't shown today, so show it
        shouldShow = true;
      }
      // If already shown today, don't show again (even if they skipped)
    } else {
      // After 3 days: Only show if user skipped AND 7 days have passed since skip
      if (dismissed === 'skipped' && dismissedDate) {
        const daysSinceDismissed = (now - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed >= 7) {
          shouldShow = true;
          // Reset the skip status to show again
          localStorage.removeItem('pwa-install-dismissed');
          localStorage.removeItem('pwa-install-dismissed-date');
        }
      } else if (!dismissed) {
        // If never skipped during first 3 days, show it after 3 days
        shouldShow = true;
      }
      // If dismissed === 'installed', shouldShow remains false (already handled above)
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (shouldShow) {
        // Show prompt after a short delay
        setTimeout(() => {
          setShowPrompt(true);
          // Track that we showed it today
          localStorage.setItem('pwa-last-shown-date', now.toString());
        }, 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS Safari and other browsers that don't support beforeinstallprompt
    // Check if it's iOS and show manual install instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);
    
    // Show prompt for mobile devices even without beforeinstallprompt
    // (for iOS Safari, we'll show manual instructions)
    if (shouldShow && (isIOS || isAndroid)) {
      setTimeout(() => {
        setShowPrompt(true);
        // Track that we showed it today
        localStorage.setItem('pwa-last-shown-date', now.toString());
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    const now = Date.now();
    
    if (deferredPrompt) {
      // Chrome/Edge - use native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        // User installed the app
        localStorage.setItem('pwa-install-dismissed', 'installed');
        localStorage.setItem('pwa-install-dismissed-date', now.toString());
        localStorage.setItem('pwa-last-shown-date', now.toString());
      } else {
        // User declined - treat as skipped
        localStorage.setItem('pwa-install-dismissed', 'skipped');
        localStorage.setItem('pwa-install-dismissed-date', now.toString());
        localStorage.setItem('pwa-last-shown-date', now.toString());
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    } else {
      // iOS Safari or other browsers - show manual instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      if (isIOS) {
        alert('To install this app on iOS:\n\n1. Tap the Share button (□↑) at the bottom\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm');
      } else {
        alert('To install this app:\n\n1. Tap the menu button (⋮) in your browser\n2. Select "Add to Home Screen" or "Install App"\n3. Follow the on-screen instructions');
      }
      
      // Mark as viewed (user saw instructions, but we don't know if they installed)
      // Treat as skipped for now - they can try again later
      localStorage.setItem('pwa-install-dismissed', 'skipped');
      localStorage.setItem('pwa-install-dismissed-date', now.toString());
      localStorage.setItem('pwa-last-shown-date', now.toString());
      setShowPrompt(false);
    }
  };

  const handleSkip = () => {
    const now = Date.now();
    localStorage.setItem('pwa-install-dismissed', 'skipped');
    localStorage.setItem('pwa-install-dismissed-date', now.toString());
    localStorage.setItem('pwa-last-shown-date', now.toString()); // Track that we showed it today
    setShowPrompt(false);
  };

  if (!isMobile || !showPrompt) {
    return null;
  }

  return (
    <Modal 
      show={showPrompt} 
      onHide={handleSkip}
      centered
      backdrop="static"
      className="pwa-install-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>📱 Install App</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="pwa-install-content">
          <p>Install <strong>बड़ेला धाम भारतवर्ष</strong> on your device for a better experience!</p>
          <ul className="pwa-install-benefits">
            <li>✅ Faster access</li>
            <li>✅ Works offline</li>
            <li>✅ No app store needed</li>
            <li>✅ Free to install</li>
          </ul>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleSkip}>
          Skip
        </Button>
        <Button variant="primary" onClick={handleInstall}>
          Install
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PWAInstallPrompt;




// WEBPACK FOOTER //
// ./src/components/PWAInstallPrompt/PWAInstallPrompt.jsx